/**
 * PromptCallTabs — one AI Review model call, as it concerns ONE contact, as
 * tabs: Request (default) / System prompt / Response / Attempts, plus Details
 * (model, route, call placement, tokens, time and cost) when asked.
 *
 * Shared by the person panel's run cards (inline, `withDetails`) and the
 * full-screen `PromptViewerModal` (whose "Who ran it" sidebar already says
 * what Details would). Mounting it reads `GET …/prompt` and `GET …/call`;
 * both are cached per review and contact, so the card and the modal share
 * one request each.
 */
import type { AiReviewContactPrompt, AiReviewPromptUnavailableReason } from "@nu-assist/contracts";
import { ProgressBar } from "@nu-assist/ui";
import { AlertTriangle } from "lucide-react";
import {
  Fragment,
  type KeyboardEvent,
  type MutableRefObject,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import {
  type AiReviewClient,
  type AiReviewContactCall,
  useAiReviewContactCall,
  useAiReviewContactPrompt,
} from "../../clients/aiReview.client";
import { fmtCost, fmtCount, fmtSeconds, routeBadge } from "../person-panel/format";
import { type ContextSpanKind, splitContextSpans } from "./contextSpans";
import { AttemptsPanel, Pre } from "./viewerParts";

export type PromptCallTab = "system" | "request" | "response" | "attempts" | "details";

/** A request to show one Thrive context block of the contact's prompt: the
 *  Request tab opens, the block scrolls into view and is highlighted for
 *  `JUMP_HIGHLIGHT_MS`. `seq` changes on every click, so clicking the same
 *  count again jumps again. */
export interface PromptJump {
  target: ContextSpanKind;
  seq: number;
}

export const JUMP_HIGHLIGHT_MS = 2000;

/** The id of a context block's span, for a link that points at it. */
export function contextSpanId(anchorId: string, kind: ContextSpanKind): string {
  return `${anchorId}-${kind}`;
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  } catch {
    return false;
  }
}

export const MISMATCH_TEXT =
  "The data behind this score changed since it was scored; this is a reconstruction.";

export const RESPONSE_NOTE = "Parsed from the model's answer — the raw text isn't stored.";

const UNAVAILABLE_TEXT: Record<AiReviewPromptUnavailableReason, string> = {
  pre_provenance:
    "This contact was scored before prompts were recorded, so its prompt cannot be rebuilt.",
  call_not_found: "The record of the model call behind this score is missing.",
  file_unreadable:
    "The people list's original file could not be read just now (or is too large to rebuild here). Try again later.",
  file_missing:
    "The people list's original file is no longer available, so the prompt cannot be rebuilt.",
  contact_not_in_file: "This contact is no longer in the people list's original file.",
  unsupported_prompt_version:
    "This score was made with an older prompt version that can no longer be rebuilt.",
};

/** The contact block, its Thrive context blocks as their own spans: a light
 *  tint always, the strong highlight while a jump points at one. */
function ContactBlock({
  text,
  highlighted,
  anchorId,
  spanRefs,
}: {
  text: string;
  highlighted: ContextSpanKind | null;
  anchorId: string | undefined;
  spanRefs: MutableRefObject<Partial<Record<ContextSpanKind, HTMLSpanElement | null>>>;
}) {
  // Each segment is keyed by where it starts in the text.
  let offset = 0;
  return (
    <Pre testId="prompt-contact-block">
      {splitContextSpans(text).map((segment) => {
        const kind = segment.kind;
        const key = offset;
        offset += segment.text.length;
        if (kind === "plain") return <Fragment key={key}>{segment.text}</Fragment>;
        const on = highlighted === kind;
        return (
          <span
            key={key}
            ref={(el) => {
              spanRefs.current[kind] = el;
            }}
            id={anchorId ? contextSpanId(anchorId, kind) : undefined}
            data-testid={`prompt-context-${kind}`}
            tabIndex={-1}
            data-highlighted={on ? "true" : undefined}
            className={`box-decoration-clone rounded-sm transition-colors motion-reduce:transition-none ${
              on ? "bg-mark text-mark-ink ring-2 ring-accent" : "bg-accent-muted"
            }`}
          >
            {segment.text}
          </span>
        );
      })}
    </Pre>
  );
}

function RequestPanel({
  prompt,
  contactName,
  jump,
  handledJump,
  anchorId,
}: {
  prompt: AiReviewContactPrompt;
  contactName: string;
  jump: PromptJump | undefined;
  /** The last jump acted on, owned by the tabs: the panel unmounts with its
   *  tab, and coming back to Request must not replay an old jump. */
  handledJump: MutableRefObject<number | null>;
  anchorId: string | undefined;
}) {
  const blockRef = useRef<HTMLDivElement>(null);
  const spanRefs = useRef<Partial<Record<ContextSpanKind, HTMLSpanElement | null>>>({});
  const [highlight, setHighlight] = useState<PromptJump | null>(null);
  const request = prompt.request;

  // Runs once the panel is on screen with the prompt, so a jump asked for
  // while the prompt was still loading lands when it arrives. No span (the
  // contact had none of that context, a 2.0.0 prompt): the contact block,
  // as "Jump to …" does. The span lands mid-screen, unless it is taller than
  // what shows it (the tab panel, capped at max-h-96 on the card, or the
  // window): centring it would then hide its opening tag. Focus moves to the
  // span so keyboard and screen-reader users land where the eye does.
  useEffect(() => {
    if (!jump || handledJump.current === jump.seq) return;
    handledJump.current = jump.seq;
    const span = spanRefs.current[jump.target];
    const behavior: ScrollBehavior = prefersReducedMotion() ? "auto" : "smooth";
    if (span) {
      const panel = span.closest<HTMLElement>("[data-prompt-scroll]");
      const room = Math.min(window.innerHeight, panel?.clientHeight || window.innerHeight);
      const fits = span.getBoundingClientRect().height <= room;
      span.scrollIntoView?.({ block: fits ? "center" : "start", behavior });
      span.focus?.({ preventScroll: true });
    } else {
      blockRef.current?.scrollIntoView?.({ block: "start", behavior });
    }
    if (span) setHighlight(jump);
  }, [jump, handledJump]);

  useEffect(() => {
    if (!highlight) return;
    const timer = window.setTimeout(() => setHighlight(null), JUMP_HIGHLIGHT_MS);
    return () => window.clearTimeout(timer);
  }, [highlight]);

  if (!request) return null;
  const hidden = request.hidden_candidates;
  return (
    <div className="flex flex-col gap-3">
      <p className="text-caption text-text-secondary" data-testid="prompt-request-scope">
        Jump to{" "}
        <button
          type="button"
          data-testid="prompt-jump"
          onClick={() => blockRef.current?.scrollIntoView?.({ block: "start" })}
          className="rounded-sm text-accent-ink underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {contactName}
        </button>
        {hidden > 0 &&
          ` · ${hidden} other ${hidden === 1 ? "contact" : "contacts"} in this call ${
            hidden === 1 ? "is" : "are"
          } hidden from this view`}
      </p>
      <Pre testId="prompt-criteria-block">{request.criteria_block}</Pre>
      <div ref={blockRef}>
        <ContactBlock
          text={request.contact_block}
          highlighted={highlight?.target ?? null}
          anchorId={anchorId}
          spanRefs={spanRefs}
        />
      </div>
      {hidden > 0 && (
        <p className="text-caption text-text-muted" data-testid="prompt-hidden-candidates">
          … {hidden} more {hidden === 1 ? "candidate" : "candidates"} in this call
        </p>
      )}
      {request.trailer && <Pre testId="prompt-trailer">{request.trailer}</Pre>}
    </div>
  );
}

function ResponsePanel({ prompt }: { prompt: AiReviewContactPrompt }) {
  if (!prompt.response) {
    return (
      <p className="text-body text-text-secondary" data-testid="prompt-response-empty">
        No stored answer points at this call for this contact.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <Pre testId="prompt-response">{JSON.stringify(prompt.response, null, 2)}</Pre>
      <p className="text-caption text-text-muted" data-testid="prompt-response-note">
        {RESPONSE_NOTE}
      </p>
    </div>
  );
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-micro uppercase tracking-wide text-text-muted">{label}</dt>
      <dd className="break-words text-caption text-text">{children}</dd>
    </div>
  );
}

/** The call's summary (what the run card's box used to show), and its ids. */
function DetailsPanel({ call }: { call: AiReviewContactCall | undefined }) {
  if (!call || call.status !== "recorded") {
    return (
      <p className="text-caption text-text-secondary" data-testid="prompt-details-unavailable">
        The model call behind this score was not recorded.
      </p>
    );
  }
  const badge = routeBadge(call.route);
  const attempts = call.attempt_count ?? call.attempts?.length ?? 0;
  const shared =
    call.rescore || call.batch_size === 1
      ? "rescored alone"
      : [
          call.package_seq != null && call.batch_idx != null
            ? `package ${call.package_seq + 1}, batch ${call.batch_idx + 1}`
            : null,
          call.batch_size != null
            ? `shared with ${call.batch_size - 1} ${call.batch_size - 1 === 1 ? "contact" : "contacts"}`
            : null,
        ]
          .filter(Boolean)
          .join(" · ");
  return (
    <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="ai-review-call-details">
      <Detail label="Model">
        <span className="break-all font-mono">{call.model_id ?? "—"}</span>
      </Detail>
      <Detail label="Answered by">
        {badge ? `${badge} profile` : (call.route ?? "—")} · {attempts}{" "}
        {attempts === 1 ? "attempt" : "attempts"}
      </Detail>
      {call.served_region && <Detail label="Region that ran it">{call.served_region}</Detail>}
      {shared && <Detail label="Call">{shared}</Detail>}
      {call.input_tokens != null && call.output_tokens != null && (
        <Detail label="Tokens">
          {fmtCount(call.input_tokens)} in · {fmtCount(call.output_tokens)} out
        </Detail>
      )}
      {(call.latency_ms_client != null || call.cost_usd_est != null) && (
        <Detail label="Time and cost">
          {[
            call.latency_ms_client != null ? fmtSeconds(call.latency_ms_client) : null,
            call.cost_usd_est != null ? `${fmtCost(call.cost_usd_est)} for the call` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </Detail>
      )}
      <Detail label="Ids">
        <span className="block break-all font-mono" data-testid="ai-review-request-id">
          req {call.request_id ?? "—"}
        </span>
        <span className="block break-all font-mono text-text-muted">
          llm_call {call.llm_call_id ?? "—"}
        </span>
      </Detail>
    </dl>
  );
}

export function PromptCallTabs({
  client,
  reviewId,
  contactId,
  contactName,
  withDetails = false,
  inline = false,
  jump,
  anchorId,
}: {
  client: AiReviewClient;
  reviewId: string;
  contactId: string;
  /** "Jump to …" in the Request tab. */
  contactName: string;
  /** Adds the Details tab (the run card; the modal has its sidebar). */
  withDetails?: boolean;
  /** Caps the panel's height so a long prompt scrolls inside the card. */
  inline?: boolean;
  /** Show a Thrive context block (the run card's "N Thrive notes" link). */
  jump?: PromptJump;
  /** Prefix of the context spans' ids, for links that point at them. */
  anchorId?: string;
}) {
  const tabsId = useId();
  const [tab, setTab] = useState<PromptCallTab>("request");
  const handledJump = useRef<number | null>(null);
  // A new jump opens Request, whichever tab is showing and whether or not the
  // prompt has loaded; the panel scrolls once it can.
  useEffect(() => {
    if (jump) setTab("request");
  }, [jump]);
  const tabRefs = useRef<Partial<Record<PromptCallTab, HTMLButtonElement | null>>>({});
  const promptQuery = useAiReviewContactPrompt({ client, reviewId, contactId });
  const callQuery = useAiReviewContactCall({ client, reviewId, contactId });
  const prompt = promptQuery.data;
  const call = callQuery.data;

  const tabs: PromptCallTab[] = ["system", "request", "response", "attempts"];
  if (withDetails) tabs.push("details");
  const attemptCount = call?.attempts?.length ?? 0;
  const tabLabel: Record<PromptCallTab, string> = {
    system: "System prompt",
    request: "Request",
    response: "Response",
    attempts: `Attempts · ${attemptCount}`,
    details: "Details",
  };

  const onTabKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const next =
      tabs[(tabs.indexOf(tab) + (e.key === "ArrowRight" ? 1 : tabs.length - 1)) % tabs.length];
    if (!next) return;
    setTab(next);
    tabRefs.current[next]?.focus();
  };

  if (promptQuery.isPending) {
    return (
      <div className="flex flex-col gap-2 py-6" data-testid="prompt-loading">
        <ProgressBar indeterminate live label="Rebuilding the prompt" />
        <p className="text-caption text-text-muted">Rebuilding the prompt…</p>
      </div>
    );
  }
  if (promptQuery.isError) {
    return (
      <p
        data-testid="prompt-error"
        className="rounded-md border border-border bg-bg-subtle px-4 py-6 text-center text-body text-text-secondary"
      >
        {promptQuery.error?.status === 404
          ? "This contact has no scores in this review."
          : "The prompt could not be loaded. Try again in a moment."}
      </p>
    );
  }
  // Unavailable: the prompt tabs have nothing to show, but the card still
  // offers Details — what is known about the call does not depend on the file.
  const unavailable = prompt?.status === "unavailable";
  if (!prompt || (unavailable && !withDetails)) {
    return (
      <p
        data-testid="prompt-unavailable"
        className="rounded-md border border-border bg-bg-subtle px-4 py-6 text-center text-body text-text-secondary"
      >
        {prompt?.reason ? UNAVAILABLE_TEXT[prompt.reason] : "This prompt cannot be shown."}
      </p>
    );
  }

  const unavailableText = (
    <p
      data-testid="prompt-unavailable"
      className="rounded-md border border-border bg-bg-subtle px-4 py-6 text-center text-body text-text-secondary"
    >
      {prompt.reason ? UNAVAILABLE_TEXT[prompt.reason] : "This prompt cannot be shown."}
    </p>
  );

  return (
    <div className="flex min-h-0 flex-col gap-3" data-testid="prompt-call-tabs">
      {prompt.status === "mismatch" && (
        <output
          data-testid="prompt-mismatch"
          className="flex items-start gap-2 rounded-md border border-warning-strong bg-warning-muted px-3 py-2 text-body text-warning-ink"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{MISMATCH_TEXT}</span>
        </output>
      )}
      <div
        role="tablist"
        aria-label="Model call part"
        className="flex gap-1 overflow-x-auto border-b border-border"
        onKeyDown={onTabKeyDown}
      >
        {tabs.map((t) => (
          <button
            key={t}
            ref={(el) => {
              tabRefs.current[t] = el;
            }}
            type="button"
            role="tab"
            id={`${tabsId}-tab-${t}`}
            aria-selected={tab === t}
            aria-controls={`${tabsId}-panel`}
            tabIndex={tab === t ? 0 : -1}
            data-testid={`prompt-tab-${t}`}
            onClick={() => setTab(t)}
            className={`whitespace-nowrap border-b-2 px-2 py-2 text-label focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
              tab === t
                ? "border-accent text-accent-ink"
                : "border-transparent text-text-secondary hover:text-text"
            }`}
          >
            {tabLabel[t]}
          </button>
        ))}
      </div>
      <div
        role="tabpanel"
        id={`${tabsId}-panel`}
        aria-labelledby={`${tabsId}-tab-${tab}`}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a scroll region must be focusable to scroll by keyboard
        tabIndex={0}
        data-prompt-scroll
        className={`min-h-0 overflow-auto rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
          inline ? "max-h-96" : ""
        }`}
      >
        {tab === "details" ? (
          <DetailsPanel call={call} />
        ) : unavailable ? (
          unavailableText
        ) : (
          <>
            {tab === "system" && <Pre testId="prompt-system">{prompt.system_prompt ?? ""}</Pre>}
            {tab === "request" && (
              <RequestPanel
                prompt={prompt}
                contactName={contactName}
                jump={jump}
                handledJump={handledJump}
                anchorId={anchorId}
              />
            )}
            {tab === "response" && <ResponsePanel prompt={prompt} />}
            {tab === "attempts" && <AttemptsPanel call={call} />}
          </>
        )}
      </div>
    </div>
  );
}
